import React, { useState, useRef, useEffect } from "react"
import { Card, CardContent, Paper, Typography, makeStyles, useTheme, Switch, IconButton } from "@material-ui/core"
import { ArrowBackIosOutlined as ArrowBack, ArrowForwardIosOutlined as ArrowForward } from "@material-ui/icons"
import { Chart } from "react-google-charts"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Skeleton } from "@material-ui/lab"
import _ from "lodash"
import { setDashboardReview } from "../../../actions/dashboardActions"

const useStyles = makeStyles((theme) => ({
	root: {
		// maxHeight: 378,
		// minHeight: 378,
		marginTop: 14,
	},
	cardBody: {
		padding: 0,
		"&:last-child": {
			paddingBottom: 0,
		},
	},
	title: {
		color: theme.palette.grey.D100,
		fontWeight: 400,
		padding: "20px 0 10px 0",
	},
	reviewName: {
		fontWeight: 500,
	},
	switches: {
		color: theme.palette.text.primary,
	},
	skeletonWrapper: {
		padding: 15,
	},
	NoData: {
		minHeight: 200,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	cardHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginTop: "4px",
	},
	resetIcon: {
		color: theme.palette.grey.D100,
		cursor: "pointer",
		marginRight: "20px",
	},
	switchesMainDiv: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-around",
		padding: "0 28px 0 28px",
	},
	switchAChecked: {
		"& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track": {
			backgroundColor: theme.palette.primary.dark,
		},
		"& .MuiIconButton-label": {
			color: theme.palette.primary.dark,
		},
	},
	switchBChecked: {
		"& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track": {
			backgroundColor: theme.palette.dashboard.D100,
		},
		"& .MuiIconButton-label": {
			color: theme.palette.dashboard.D100,
		},
	},
	switchCChecked: {
		"& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track": {
			backgroundColor: theme.palette.dashboard.D110,
		},
		"& .MuiIconButton-label": {
			color: theme.palette.dashboard.D110,
		},
	},
	dueDateMain: {
		display: "flex",
		alignItems: "center",
		marginTop: 24,
		paddingRight: 24,
	},
	tooltipDiv: {
		display: "flex",
		alignItems: "center",
		padding: "2px 0",
		gap: "5px",
	},
	tooltipLabel: {
		...theme.typography.caption,
		color: theme.palette.grey.D100,
		whiteSpace: "nowrap",
		paddingLeft: 2,
	},
	tooltipValue: {
		...theme.typography.caption,
		color: theme.palette.text.primary,
		marginLeft: 3,
		fontWeight: theme.typography.fontWeightMedium,
		whiteSpace: "nowrap",
	},
	tooltipTopDiv: {
		display: "flex",
		alignItems: "center",
	},
	tooltipCircleIcon: {
		height: 8,
		width: 8,
		borderRadius: 0,
		marginLeft: 5,
	},
	pagination: {
		display: "flex",
		alignItems: "center",
		justifyContent: "end",
		gap: 5,
		padding: "5px 10px",
		"& svg": {
			cursor: "pointer",
		},
	},
	tooltipTitle: {
		fontSize: 12,
		fontWeight: 500,
		textWrap: "nowrap",
	},
	tooltipSubTitle: {
		fontSize: 12,
		fontWeight: 400,
		color: theme.palette.grey.D100,
	},
	tooltipWrapper: {
		padding: "8px 6px",
		borderRadius: 3,
	},
}))

const UserReviewOverview = ({ getUserReviewDetails }) => {
	const classes = useStyles()
	const theme = useTheme()
	const isSenderSelected = useRef(null)
	const [t] = useTranslation("global")
	const dispatch = useDispatch()
	const colors = [theme.palette.dashboard.D110, theme.palette.dashboard.D100]
	const types = { REVIEWED: "Reviewed files", TOTAL: "Total files" }
	const typesColor = { REVIEWED: theme.palette.dashboard.D100, TOTAL: theme.palette.dashboard.D110 }

	const mapDomToTooltip = (dueDate, data, type) => {
		switch (type) {
			case types.REVIEWED:
				return `<div class="${classes.tooltipTopDiv}">
						<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.REVIEWED}">
						</div>
						<div style="padding:6px">
							<div class="${classes.tooltipDiv}">
								<span class="${classes.tooltipLabel}">${t("dashboard.reviewedFiles")}:</span>
								<span class="${classes.tooltipValue}">${data}</span>
							</div>
						</div>
					</div>`
			case types.TOTAL: {
				return `<div class="${classes.tooltipTopDiv}">
						<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.TOTAL}">
						</div>
						<div style="padding:6px">
							<div class="${classes.tooltipDiv}">
								<span class="${classes.tooltipLabel}">${t("tableColumns.totalFiles")}:</span>
								<span class="${classes.tooltipValue}">${data}</span>
							</div>
						</div>
					</div>`
			}
			default:
				return ""
		}
	}

	const mapDataToTooltip = (dueDate, data) => {
		return `<div class="${classes.tooltipWrapper}">
				<div class="${classes.tooltipTitle}">${data[0]}</div>
				<div class="${classes.tooltipSubTitle}">${t("dashboard.status")}</div>

				<div class="${classes.tooltipTopDiv}" style="padding-top:0">
					<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.TOTAL}"></div>
					<div class="${classes.tooltipDiv}">
						<span class="${classes.tooltipLabel}">${t("tableColumns.totalFiles")}:</span>
						<span class="${classes.tooltipValue}">${data[2]}</span>
					</div>
				</div>

				<div class="${classes.tooltipTopDiv}">
					<div class="${classes.tooltipCircleIcon}" style="background-color:${typesColor.REVIEWED}"></div>
					<div class="${classes.tooltipDiv}">
						<span class="${classes.tooltipLabel}">${t("dashboard.reviewedFiles")}:</span>
						<span class="${classes.tooltipValue}">${data[1]}</span>
					</div>
				</div>
			</div>`
	}

	const addCustomToolTip = (chartTempData) => {
		const initializeArray = [chartTempData?.[0]?.[0]]
		if (chartTempData?.[0]?.[2]) {
			initializeArray.push(...[chartTempData[0][2], { role: "tooltip", p: { html: true } }])
		}
		if (chartTempData?.[0]?.[1]) {
			initializeArray.push(...[chartTempData[0][1], { role: "tooltip", p: { html: true } }])
		}
		return chartTempData.reduce(
			(result, currItem, index) => {
				const currArray = [currItem[0]]
				if (index !== 0) {
					if (currItem?.[2] >= 0) {
						currArray.push(...[currItem[2], mapDataToTooltip("2009-12-29T19:54:15", currItem)])
					}
					if (currItem?.[1] >= 0) {
						currArray.push(...[currItem[1], mapDataToTooltip("2009-12-29T19:54:15", currItem)])
					}
					result.push(currArray)
				}
				return result
			},
			[[...initializeArray]]
		)
	}

	const prepareData = (data, firstIndex, lastIndex) => {
		const cloneData = data?.length ? _.clone(data) : []
		const headers = cloneData[0]

		const newData = cloneData.slice(firstIndex, lastIndex)
		if (firstIndex !== 0) {
			newData.unshift(headers)
		}

		return newData
	}
	const reviewOverview = useSelector((state) => state.info?.reviewOverviewDetails?.review_overview || [])
	const isLoading = useSelector((state) => state.info?.isUserReviewOverviewLoading)
	const data = useSelector((state) => state.info?.userReviewOverviewDetails?.review_overview)
	const dateData = useSelector((state) => state.info?.userReviewOverviewDetails?.review_overview_due_date || {})
	const reviewList = useSelector((state) => state.review?.allReviewlist)
	const selectedReview = useSelector((state) => state.dashboard?.selectedReview)

	const [graphColors, setGraphColors] = useState([...colors])
	const [chartData, setChartData] = useState([])
	const [currentPage, setCurrentPage] = useState(1)
	const itemPerPage = 9

	const nextPage = () => {
		if (currentPage < Math.ceil(chartData.length / itemPerPage)) {
			setCurrentPage((prev) => currentPage + 1)
		}
	}

	const prevPage = () => {
		if (currentPage > 1) {
			setCurrentPage(currentPage - 1)
		}
	}

	useEffect(() => {
		setChartData((prev) => (data?.length ? addCustomToolTip(data) : []))
	}, [data])

	const indexOfLastItem = currentPage * itemPerPage
	const indexOfFirstitem = indexOfLastItem - itemPerPage
	const currentItems = prepareData(chartData, indexOfFirstitem, indexOfLastItem)

	const options = {
		title: "",
		tooltip: {
			isHtml: true,
		},
		height: 400,
		legend: {
			position: "bottom",
			alignment: "end",
			labels: {
				boxWidth: 5,
				usePointStyle: true,
			},
			textStyle: {
				fontSize: 12,
			},
		},
		animation: {
			startup: true,
			duration: 1000,
			easing: "inAndOut",
		},
		colors: graphColors,
		chartArea: {
			left: 40,
			right: 40,
			top: 10,
			bottom: 50,
			width: "100%",
		},
		//isStacked: true,
		vAxis: {
			baselineColor: "red",
			gridlines: {
				color: "none",
				zeroLineColor: "transparent",
			},
			baselineColor: theme.palette.grey[300],
		},
		bar: { groupWidth: 30 },
		seriesType: "bars",
		fontSize: 12,
	}

	return (
		<Card className={classes.root} elevation={0}>
			<CardContent className={classes.cardBody}>
				{isLoading ? (
					<div className={classes.skeletonWrapper}>
						<Skeleton variant="rect" width={"100%"} height={300} />
					</div>
				) : chartData?.length > 1 ? (
					<>
						<Chart chartType="ComboChart" data={currentItems} options={options} />

						<div className={classes.pagination} style={{ visibility: chartData?.length > itemPerPage ? "" : "hidden" }}>
							<IconButton onClick={prevPage} color="primary" aria-label="prev" disabled={currentPage === 1} title="Previous">
								<ArrowBack />
							</IconButton>
							<IconButton
								onClick={nextPage}
								color="primary"
								aria-label="next"
								disabled={currentPage === Math.ceil(chartData?.length / itemPerPage)}
								title="Next"
							>
								<ArrowForward />
							</IconButton>
						</div>
					</>
				) : (
					<Typography variant="subtitle1" className={classes.NoData}>
						{t("dashboard.noDataFound")}
					</Typography>
				)}
			</CardContent>
		</Card>
	)
}

export default React.memo(UserReviewOverview)
